import React, { useState, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { 
  Camera, X, Upload, Loader2, ChevronLeft, ChevronRight,
  Trash2, User, Calendar, Image, CheckCircle, Globe, Users, Flag
} from 'lucide-react';
import ReportModal from './ReportModal';

const MAX_PHOTOS_PER_UPLOAD = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function PhotoGallery({ barId, barName }) {
  const { getBarPhotos, uploadPhoto, deletePhoto } = useApp();
  const { user, profile } = useAuth();
  const fileInputRef = useRef(null);
  
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [reportingPhoto, setReportingPhoto] = useState(null);
  
  // Multiple files state
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const photos = getBarPhotos(barId);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setError('');
    setUploadSuccess(false);
    
    // Check total count
    if (files.length > MAX_PHOTOS_PER_UPLOAD) {
      setError(`You can only upload ${MAX_PHOTOS_PER_UPLOAD} photos at a time`);
      return;
    }

    // Validate each file
    const validFiles = [];
    const errors = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" is ${formatFileSize(file.size)}. Max size is 5MB.`);
        continue;
      }

      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported format. Use JPG, PNG, or WebP.`);
        continue;
      }

      validFiles.push({
        file,
        preview: URL.createObjectURL(file),
        caption: '',
        visibility: 'public', // 'public' or 'friends'
        id: `${file.name}-${Date.now()}-${Math.random()}`
      });
    }

    if (errors.length > 0) {
      setError(errors.join(' '));
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setShowUploadForm(true);
    }
  };

  const removeFile = (fileId) => {
    setSelectedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      // Revoke object URL to free memory
      const removed = prev.find(f => f.id === fileId);
      if (removed) URL.revokeObjectURL(removed.preview);
      return updated;
    });
    
    if (selectedFiles.length <= 1) {
      setShowUploadForm(false);
    }
  };

  const updateCaption = (fileId, caption) => {
    setSelectedFiles(prev => 
      prev.map(f => f.id === fileId ? { ...f, caption } : f)
    );
  };

  const updateVisibility = (fileId, visibility) => {
    setSelectedFiles(prev => 
      prev.map(f => f.id === fileId ? { ...f, visibility } : f)
    );
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    setError('');
    setUploadProgress({});
    
    const results = { success: 0, failed: 0 };
    const errors = [];

    // Upload files sequentially to avoid overwhelming the server
    for (const fileData of selectedFiles) {
      setUploadProgress(prev => ({ ...prev, [fileData.id]: 'uploading' }));
      
      try {
        await uploadPhoto(barId, fileData.file, fileData.caption.trim(), fileData.visibility);
        setUploadProgress(prev => ({ ...prev, [fileData.id]: 'success' }));
        results.success++;
      } catch (err) {
        setUploadProgress(prev => ({ ...prev, [fileData.id]: 'error' }));
        errors.push(`Failed to upload "${fileData.file.name}": ${err.message}`);
        results.failed++;
      }
    }

    setUploading(false);

    if (results.success > 0) {
      setUploadSuccess(true);
      
      // Clean up after successful uploads
      setTimeout(() => {
        // Revoke all preview URLs
        selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
        setSelectedFiles([]);
        setShowUploadForm(false);
        setUploadProgress({});
        setUploadSuccess(false);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);
    }

    if (errors.length > 0) {
      setError(errors.join(' '));
    }
  };

  const handleDelete = async (photo) => {
    if (!confirm('Delete this photo?')) return;
    
    try {
      await deletePhoto(photo.id, photo.storagePath);
      setSelectedPhoto(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const cancelUpload = () => {
    // Revoke all preview URLs
    selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setSelectedFiles([]);
    setShowUploadForm(false);
    setError('');
    setUploadProgress({});
    setUploadSuccess(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const navigatePhoto = (direction) => {
    const currentIndex = photos.findIndex(p => p.id === selectedPhoto.id);
    const newIndex = direction === 'next' 
      ? (currentIndex + 1) % photos.length
      : (currentIndex - 1 + photos.length) % photos.length;
    setSelectedPhoto(photos[newIndex]);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Camera className="w-5 h-5 text-dive-neon" />
          Photos
          {photos.length > 0 && (
            <span className="text-gray-400 text-sm font-normal">({photos.length})</span>
          )}
        </h3>
        
        {user && (
          <label className="btn-secondary text-sm flex items-center gap-2 cursor-pointer">
            <Camera className="w-4 h-4" />
            Add Photos
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileSelect}
              multiple
              className="hidden"
            />
          </label>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Upload Preview - Multiple Photos */}
      {showUploadForm && selectedFiles.length > 0 && (
        <div className="bg-dive-darker rounded-xl border border-dive-gray p-4 mb-4">
          {/* Success message */}
          {uploadSuccess && (
            <div className="flex items-center gap-2 text-green-400 mb-4 p-3 bg-green-500/10 rounded-lg">
              <CheckCircle className="w-5 h-5" />
              <span>{selectedFiles.filter(f => uploadProgress[f.id] === 'success').length} photo(s) uploaded successfully!</span>
            </div>
          )}
          
          {/* Photo previews grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
            {selectedFiles.map((fileData) => (
              <div 
                key={fileData.id}
                className={`relative bg-dive-dark rounded-lg overflow-hidden border ${
                  uploadProgress[fileData.id] === 'success' 
                    ? 'border-green-500' 
                    : uploadProgress[fileData.id] === 'error'
                    ? 'border-red-500'
                    : 'border-dive-gray'
                }`}
              >
                {/* Preview image */}
                <div className="aspect-square relative">
                  <img
                    src={fileData.preview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Upload status overlay */}
                  {uploadProgress[fileData.id] && (
                    <div className={`absolute inset-0 flex items-center justify-center ${
                      uploadProgress[fileData.id] === 'uploading' ? 'bg-black/50' : ''
                    }`}>
                      {uploadProgress[fileData.id] === 'uploading' && (
                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                      )}
                      {uploadProgress[fileData.id] === 'success' && (
                        <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                      )}
                      {uploadProgress[fileData.id] === 'error' && (
                        <div className="absolute top-2 right-2 bg-red-500 rounded-full p-1">
                          <X className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Remove button */}
                  {!uploading && !uploadProgress[fileData.id] && (
                    <button
                      onClick={() => removeFile(fileData.id)}
                      className="absolute top-2 right-2 p-1 bg-black/70 rounded-full text-white hover:bg-black"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* File info */}
                <div className="p-2">
                  <p className="text-white text-xs truncate">{fileData.file.name}</p>
                  <p className="text-gray-500 text-xs">{formatFileSize(fileData.file.size)}</p>
                  
                  {/* Caption and visibility */}
                  {!uploading && !uploadProgress[fileData.id] && (
                    <>
                      <input
                        type="text"
                        value={fileData.caption}
                        onChange={(e) => updateCaption(fileData.id, e.target.value)}
                        placeholder="Caption (optional)"
                        className="w-full mt-2 text-xs py-1 px-2"
                      />
                      
                      {/* Visibility toggle */}
                      <div className="flex gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => updateVisibility(fileData.id, 'public')}
                          className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded text-xs transition-colors ${
                            fileData.visibility === 'public'
                              ? 'bg-dive-neon text-white'
                              : 'bg-dive-darker text-gray-400 hover:text-white'
                          }`}
                        >
                          <Globe className="w-3 h-3" />
                          Public
                        </button>
                        <button
                          type="button"
                          onClick={() => updateVisibility(fileData.id, 'friends')}
                          className={`flex-1 flex items-center justify-center gap-1 py-1 px-2 rounded text-xs transition-colors ${
                            fileData.visibility === 'friends'
                              ? 'bg-blue-500 text-white'
                              : 'bg-dive-darker text-gray-400 hover:text-white'
                          }`}
                        >
                          <Users className="w-3 h-3" />
                          Friends
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Action buttons */}
          {!uploadSuccess && (
            <div className="flex gap-2">
              <button
                onClick={cancelUpload}
                disabled={uploading}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload {selectedFiles.length} Photo{selectedFiles.length > 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Photo Grid */}
      {photos.length === 0 ? (
        <div className="text-center py-8 bg-dive-darker rounded-xl border border-dashed border-dive-gray">
          <Image className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 mb-1">No photos yet</p>
          {user ? (
            <p className="text-gray-500 text-sm">Be the first to add a photo!</p>
          ) : (
            <p className="text-gray-500 text-sm">Sign in to add photos</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.slice(0, 8).map((photo, index) => (
            <button
              key={photo.id}
              onClick={() => setSelectedPhoto(photo)}
              className="aspect-square rounded-lg overflow-hidden bg-dive-darker hover:ring-2 hover:ring-dive-neon transition-all"
            >
              <img
                src={photo.url}
                alt={photo.caption || barName}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
          {photos.length > 8 && (
            <button
              onClick={() => setSelectedPhoto(photos[8])}
              className="aspect-square rounded-lg bg-dive-darker flex items-center justify-center text-gray-400 hover:text-white transition-colors"
            >
              <span className="text-sm font-medium">+{photos.length - 8} more</span>
            </button>
          )}
        </div>
      )}

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation */}
          {photos.length > 1 && (
            <>
              <button
                onClick={() => navigatePhoto('prev')}
                className="absolute left-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => navigatePhoto('next')}
                className="absolute right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Photo */}
          <div className="max-w-4xl max-h-[80vh] w-full mx-4">
            <img
              src={selectedPhoto.url}
              alt={selectedPhoto.caption || barName}
              className="w-full h-full object-contain rounded-lg"
            />
            
            {/* Photo info */}
            <div className="mt-4 flex items-center justify-between">
              <div className="text-white/70 text-sm flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {selectedPhoto.userName}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(selectedPhoto.createdAt)}
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Report button (only for other users' photos) */}
                {user && user.id !== selectedPhoto.userId && (
                  <button
                    onClick={() => setReportingPhoto(selectedPhoto)}
                    className="flex items-center gap-1 text-gray-400 hover:text-red-400 text-sm"
                  >
                    <Flag className="w-4 h-4" />
                    Report
                  </button>
                )}
                {/* Delete button (only for own photos or admins) */}
                {(user?.id === selectedPhoto.userId || profile?.is_admin) && (
                  <button
                    onClick={() => handleDelete(selectedPhoto)}
                    className="flex items-center gap-1 text-red-400 hover:text-red-300 text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Caption */}
            {selectedPhoto.caption && (
              <p className="mt-2 text-white/90">{selectedPhoto.caption}</p>
            )}
          </div>
        </div>
      )}

      {/* Report Photo Modal */}
      <ReportModal
        isOpen={!!reportingPhoto}
        onClose={() => setReportingPhoto(null)}
        contentType="photo"
        contentId={reportingPhoto?.id}
        contentPreview={reportingPhoto?.caption || `Photo by ${reportingPhoto?.userName}`}
      />
    </div>
  );
}

export default PhotoGallery;

